/**
 * GUMSHOE — QA automation API. Frozen core.
 *
 * Exposes window.__gumshoe with state/teleport/collect/accuse/shot.
 * Modules register their implementations through the DebugRegistry:
 *   - GAME registers state(), collect(), accuse()
 *   - PLAYER registers teleport()
 *   - WORLD registers the named cinematic shot positions
 */
import type {
  DebugRegistry,
  GameState,
  GumshoeDebugApi,
  ShotDef,
  ShotName
} from './types'

declare global {
  interface Window {
    __gumshoe: GumshoeDebugApi
  }
}

interface DebugWiring {
  readonly registry: DebugRegistry
  readonly api: GumshoeDebugApi
}

const warnUnregistered = (capability: string): void => {
  console.warn(`[gumshoe:debug] '${capability}' has not been registered yet`)
}

export const createDebugApi = (): DebugWiring => {
  let stateFn: (() => GameState) | null = null
  let teleportFn: ((x: number, y: number, z: number, yaw?: number, pitch?: number) => void) | null =
    null
  let collectFn: ((clueId: string) => void) | null = null
  let accuseFn: ((suspectId: string) => void) | null = null
  const shots = new Map<ShotName, ShotDef>()

  const registry: DebugRegistry = {
    registerState(fn) {
      stateFn = fn
    },
    registerTeleport(fn) {
      teleportFn = fn
    },
    registerCollect(fn) {
      collectFn = fn
    },
    registerAccuse(fn) {
      accuseFn = fn
    },
    registerShot(shot) {
      shots.set(shot.name, shot)
    }
  }

  const api: GumshoeDebugApi = {
    state(): GameState | null {
      if (!stateFn) {
        warnUnregistered('state')
        return null
      }
      return stateFn()
    },
    teleport(x, y, z, yaw, pitch): void {
      if (!teleportFn) {
        warnUnregistered('teleport')
        return
      }
      teleportFn(x, y, z, yaw, pitch)
    },
    collect(clueId): void {
      if (!collectFn) {
        warnUnregistered('collect')
        return
      }
      collectFn(clueId)
    },
    accuse(suspectId): void {
      if (!accuseFn) {
        warnUnregistered('accuse')
        return
      }
      accuseFn(suspectId)
    },
    shot(name): void {
      const shot = shots.get(name)
      if (!shot) {
        warnUnregistered(`shot:${name}`)
        return
      }
      if (!teleportFn) {
        warnUnregistered('teleport')
        return
      }
      teleportFn(shot.position.x, shot.position.y, shot.position.z, shot.yaw, shot.pitch)
    }
  }

  return { registry, api }
}

export const installDebugApi = (api: GumshoeDebugApi): void => {
  window.__gumshoe = api
}
